import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { useId } from 'react';
import { I } from './Icons';

/** Stavební prvky s třídami z prototypu. Markup je 1:1, jen v Reactu. */

export function Surface({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <section className={`surface ${className}`.trim()}>{children}</section>;
}

export function H2({ children }: { children: ReactNode }) {
  return <div className="h2">{children}</div>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Hint({ children, style }: { children: ReactNode; style?: React.CSSProperties }) {
  return (
    <p className="hint" style={style}>
      {children}
    </p>
  );
}

/** Řádek seznamu; s onClick je to tlačítko s šipkou. */
export function Row({
  children,
  onClick,
  chevron = true,
  className = '',
}: {
  children: ReactNode;
  onClick?: () => void;
  chevron?: boolean;
  className?: string;
}) {
  if (onClick) {
    return (
      <button type="button" className={`row ${className}`.trim()} onClick={onClick}>
        {children}
        {chevron && I.chev}
      </button>
    );
  }
  return <div className={`row ${className}`.trim()}>{children}</div>;
}

export type BtnVariant = 'primary' | 'secondary' | 'danger' | 'danger-solid' | 'ghost';

type BtnProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> & {
  variant?: BtnVariant | 'default';
  /** malé tlačítko do řádků a lišt */
  sm?: boolean;
  full?: boolean;
  /** běží akce: tlačítko nejde stisknout a ukazuje kolečko */
  loading?: boolean;
  type?: 'button' | 'submit';
};

function btnClass(variant: BtnProps['variant'], sm?: boolean, full?: boolean, className?: string) {
  const v = !variant || variant === 'default' || variant === 'secondary' ? '' : variant;
  return ['btn', v, sm ? 'sm' : '', full ? 'full' : '', className].filter(Boolean).join(' ');
}

/** Jediné tlačítko aplikace – v sidebaru, na kartách, ve formulářích i v sheetech. */
export function Btn({
  variant = 'secondary',
  sm,
  full,
  loading,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: BtnProps) {
  return (
    <button
      type={type}
      className={btnClass(variant, sm, full, className)}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      {...rest}
    >
      {children}
      {loading && <span className="btn-spin" aria-hidden="true" />}
    </button>
  );
}

/** Tlačítko, které otevírá výběr souboru; vypadá a chová se jako ostatní. */
export function FileBtn({
  variant = 'secondary',
  sm,
  full,
  loading,
  label,
  accept,
  multiple,
  onFiles,
  className,
}: Pick<BtnProps, 'variant' | 'sm' | 'full' | 'loading' | 'className'> & {
  label: string;
  accept?: string;
  multiple?: boolean;
  onFiles: (files: File[]) => void;
}) {
  return (
    <label className={btnClass(variant, sm, full, className)} aria-busy={loading || undefined}>
      {label}
      <input
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={loading}
        onChange={(e) => {
          onFiles(Array.from(e.target.files ?? []));
          e.target.value = '';
        }}
      />
      {loading && <span className="btn-spin" aria-hidden="true" />}
    </label>
  );
}

export function Pill({ kind, children }: { kind: string; children: ReactNode }) {
  return <span className={`pill ${kind}`}>{children}</span>;
}

export function Tag({ children, ok }: { children: ReactNode; ok?: boolean }) {
  return <span className={`tag ${ok ? 'ok' : ''}`.trim()}>{children}</span>;
}

export function Chip({
  on,
  children,
  onClick,
}: {
  on?: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button type="button" className={`chip ${on ? 'on' : ''}`.trim()} onClick={onClick}>
      {children}
    </button>
  );
}

type FieldProps = InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string };

/** Popisek + input; label je svázaný přes id, aby šel najít i čtečkou. */
export function Field({ label, error, id, className = '', ...rest }: FieldProps) {
  const auto = useId();
  const inputId = id ?? auto;
  return (
    <div className={`field ${className}`.trim()}>
      <label htmlFor={inputId}>{label}</label>
      <input id={inputId} {...rest} />
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}

type SelectFieldProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label: string;
  /** popisek jen pro čtečky – pole v řádku tabulky, kde je význam zřejmý ze sloupce */
  hideLabel?: boolean;
};

export function SelectField({ label, id, children, hideLabel, ...rest }: SelectFieldProps) {
  const auto = useId();
  const selectId = id ?? auto;
  return (
    <div className="field">
      <label htmlFor={selectId} className={hideLabel ? 'sr-only' : undefined}>
        {label}
      </label>
      <select id={selectId} {...rest}>
        {children}
      </select>
    </div>
  );
}

type TextareaFieldProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string };

/** Víceřádkové pole se stejným rámečkem a popiskem jako ostatní pole. */
export function TextareaField({ label, id, className = '', ...rest }: TextareaFieldProps) {
  const auto = useId();
  const areaId = id ?? auto;
  return (
    <div className={`field ${className}`.trim()}>
      <label htmlFor={areaId}>{label}</label>
      <textarea id={areaId} {...rest} />
    </div>
  );
}

export function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="switch">
      <input
        type="checkbox"
        checked={checked}
        aria-label={label}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span />
    </label>
  );
}

/** Řádek s přepínačem: nadpis, popis, switch vpravo. */
export function SwitchRow({
  title,
  subtitle,
  checked,
  onChange,
  className = '',
}: {
  title: string;
  subtitle?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  className?: string;
}) {
  // celý řádek je popisek přepínače – na telefonu stačí klepnout kamkoli do řádku
  return (
    <label className={`rowx switch-row ${className}`.trim()}>
      <span className="grow">
        <span className="l1">{title}</span>
        {subtitle && <span className="l2">{subtitle}</span>}
      </span>
      <span className="switch">
        <input
          type="checkbox"
          checked={checked}
          aria-label={title}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span />
      </span>
    </label>
  );
}

export function Search({
  value,
  onChange,
  placeholder,
  inline,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  inline?: boolean;
}) {
  return (
    <div className={`search ${inline ? 'in' : ''}`.trim()}>
      {I.search}
      <input
        type="search"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function Swatch({
  color,
  on,
  onClick,
}: {
  color: string;
  on?: boolean;
  onClick?: () => void;
}) {
  if (!onClick) return <span className="swatch" style={{ background: color }} />;
  return (
    <button
      type="button"
      className={`swatch ${on ? 'on' : ''}`.trim()}
      style={{ background: color }}
      onClick={onClick}
      aria-label={`Barva ${color}`}
    />
  );
}
